/**
 * gsu-order-fees.js  (v14 = live v13 + seasonal GO+ codes)
 * -----------------------------------------------------------------------------
 * 1. GO+ (handle 'membership') can be in the cart only ONCE:
 *    - one line with quantity > 1  → set to 1
 *    - several GO+ lines (e.g. different selling plans) → keep the first, remove the rest
 *
 * 2. Adds the hidden `order-fees` product ($9.98) when ALL of these hold:
 *    - the cart has payable items,
 *    - payable subtotal (excluding the fee) is under $49,
 *    - the customer is NOT a GO+ member (window.GSU_IS_MEMBER, from tag 'gostockup-plus'),
 *    - GO+ is NOT in the cart.
 *    Removes it otherwise.
 *
 * 3. GO+ promo codes:
 *    - GO+ in cart     → GO_PLUS_CODE + active EXTRA_CODES are added to the cart.
 *    - GO+ not in cart → those codes are removed; other codes are kept.
 *
 * 4. When it runs (v13):
 *    - on page load, on theme pub-sub cartUpdate, and ~250 ms after ANY other script
 *      POSTs to /cart/add|change|update|clear (fetch or XHR). Our own requests don't count.
 *    - The checkout button is NOT intercepted anymore. Magical Mandatory Fees already
 *      intercepts it; two interceptors re-submitting each other looped forever.
 *      The cart is kept reconciled in the background instead.
 *
 * window.gsuReconcileFees() returns a Promise (used by the GO+ upsell blocks).
 * -----------------------------------------------------------------------------
 */
(function () {
  'use strict';

  // ─── CONFIG ─────────────────────────────────────────────────────────────────────
  var FEE_HANDLE        = 'order-fees';
  var FEE_VARIANT_ID    = 49272811421940;
  var MEMBERSHIP_HANDLE = 'membership';
  var GO_PLUS_CODE      = 'GSUPLUS10';
  // Seasonal codes added together with GO_PLUS_CODE when GO+ is in the cart (new members
  // don't have the tag yet, so tag-based automatic discounts don't reach them).
  var EXTRA_CODES       = [
    { code: 'GOPLUSHALLOWEEN', until: '2026-11-03T05:00:00Z' }   // GO+ Halloween: extra 17% on collection halloween-1 (25%+ total with the 10% sale price)
  ];
  var FEE_THRESHOLD     = 4900;   // cents
  var SUPPRESS_MS       = 700;
  var DEBOUNCE_MS       = 250;    // wait after a foreign cart change before re-checking
  // ───────────────────────────────────────────────────────────────────────

  var busy = false, suppressUntil = 0, running = null, timer = null;
  var promoAddTried = false;
  var dirty = true;

  // ─── Detect cart changes made by other scripts ──────────────────────────
  var rawFetch = window.fetch.bind(window);
  var CART_MUTATION = /\/cart\/(add|change|update|clear)(\.js)?(\?|$)/;

  // /cart/update that only touches attributes/note doesn't change lines, prices or codes.
  function isAttributesOnlyUpdate(url, body) {
    if (!/\/cart\/update/.test(url) || typeof body !== 'string') return false;
    return !/updates|discount|quantity|id=/.test(body);
  }

  function onForeignRequest(url, method, body) {
    if (String(method || 'GET').toUpperCase() !== 'POST') return;
    url = String(url || '');
    if (!CART_MUTATION.test(url)) return;
    if (isAttributesOnlyUpdate(url, body)) return;
    dirty = true;
    schedule();
  }

  window.fetch = function (input, init) {
    try {
      var url = (typeof Request !== 'undefined' && input instanceof Request) ? input.url : String(input);
      var method = (init && init.method) || (input && input.method) || 'GET';
      var body = init && init.body;
      if (body && typeof body !== 'string' && typeof URLSearchParams !== 'undefined' && body instanceof URLSearchParams) body = body.toString();
      onForeignRequest(url, method, body);
    } catch (e) {}
    return rawFetch.apply(window, arguments);
  };

  var xhrOpen = XMLHttpRequest.prototype.open, xhrSend = XMLHttpRequest.prototype.send;
  XMLHttpRequest.prototype.open = function (method, url) {
    this.__gsuReq = [method, url];
    return xhrOpen.apply(this, arguments);
  };
  XMLHttpRequest.prototype.send = function (body) {
    try { if (this.__gsuReq) onForeignRequest(this.__gsuReq[1], this.__gsuReq[0], body); } catch (e) {}
    return xhrSend.apply(this, arguments);
  };

  // Fires after the foreign request has had time to complete.
  function schedule() {
    clearTimeout(timer);
    timer = setTimeout(function () {
      if (busy) return;               // the running check re-schedules itself if still dirty
      reconcile(false);
    }, DEBOUNCE_MS);
  }
  // ───────────────────────────────────────────────────────────────────────

  function isMember() {
    return typeof window.GSU_IS_MEMBER !== 'undefined' && window.GSU_IS_MEMBER === true;
  }

  function cartUrl()  { return (window.routes && routes.cart_url) ? routes.cart_url : '/cart'; }
  function getCart()  { return rawFetch('/cart.js', { headers: { Accept: 'application/json' } }).then(function (r) { return r.json(); }); }
  function post(u, b) {
    return rawFetch(u, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(b)
    });
  }

  function refreshCartUI() {
    var base = cartUrl();
    rawFetch(base + '?section_id=cart-drawer').then(function (r) { return r.text(); }).then(function (txt) {
      var doc = new DOMParser().parseFromString(txt, 'text/html');
      ['cart-drawer-items', '.cart-drawer__footer'].forEach(function (sel) {
        var t = document.querySelector(sel), s = doc.querySelector(sel);
        if (t && s) t.innerHTML = s.innerHTML;
      });
    }).catch(function () {});
    rawFetch(base + '?section_id=cart-icon-bubble').then(function (r) { return r.text(); }).then(function (txt) {
      var doc = new DOMParser().parseFromString(txt, 'text/html');
      var t = document.getElementById('cart-icon-bubble'), s = doc.getElementById('cart-icon-bubble') || doc.body;
      if (t && s) t.innerHTML = s.innerHTML;
    }).catch(function () {});
    rawFetch(base + '?section_id=cart-icon-quick').then(function (r) { return r.text(); }).then(function (txt) {
      var host = document.getElementById('cart-icon-quick');
      if (!host) return;
      var doc = new DOMParser().parseFromString(txt, 'text/html');
      var t = host.querySelector('.shopify-section') || host;
      var s = doc.querySelector('.shopify-section') || doc.body;
      t.innerHTML = s.innerHTML;
      var counter = t.querySelector('[data-cartitem]'), quick = document.querySelector('.quick-notify');
      if (counter && quick && !document.querySelector('.drawer-quick-notify')) {
        quick.classList.toggle('active', parseInt(counter.getAttribute('data-cartitem'), 10) > 0);
      }
    }).catch(function () {});
    // let the /cart page summary (order summary, totals, GO+ block) refresh too
    try { document.dispatchEvent(new CustomEvent('cart:refresh')); } catch (e) {}
  }

  // On the /cart page the items list is server-rendered → reload so the fixed quantity shows.
  function isCartPage() {
    var p = window.location.pathname.replace(/\/$/, '');
    return /\/cart$/.test(p);
  }

  // GO+ only once: returns a function that fixes the cart, or null if it's fine.
  function membershipOp(cart) {
    var lines = (cart.items || []).filter(function (i) { return i.handle === MEMBERSHIP_HANDLE; });
    if (!lines.length) return null;
    if (lines.length === 1 && lines[0].quantity === 1) return null;

    var updates = {};
    lines.forEach(function (l, idx) { updates[l.key] = idx === 0 ? 1 : 0; });
    return function () { return post('/cart/update.js', { updates: updates }); };
  }

  // Codes that go with GO+ in the cart: GSUPLUS10 always, plus seasonal ones while active.
  function wantedCodes() {
    var now = Date.now();
    return [GO_PLUS_CODE].concat(EXTRA_CODES.filter(function (x) {
      return (!x.from || now >= Date.parse(x.from)) && (!x.until || now < Date.parse(x.until));
    }).map(function (x) { return x.code; }));
  }

  function promoOp(cart, memberInCart) {
    var managed = [GO_PLUS_CODE].concat(EXTRA_CODES.map(function (x) { return x.code; }))
      .map(function (c) { return c.toUpperCase(); });
    var want    = memberInCart ? wantedCodes().map(function (c) { return c.toUpperCase(); }) : [];
    var codes   = (cart.discount_codes || []).map(function (d) { return String(d.code); });
    var upper   = codes.map(function (c) { return c.toUpperCase(); });
    var missing = want.filter(function (c) { return upper.indexOf(c) === -1; });
    var extra   = upper.filter(function (c) { return managed.indexOf(c) !== -1 && want.indexOf(c) === -1; });
    if (!memberInCart) promoAddTried = false;

    if (missing.length) {
      if (promoAddTried) return null;   // Shopify rejected a code → don't loop
      promoAddTried = true;
      var keep = codes.filter(function (c) { return extra.indexOf(c.toUpperCase()) === -1; });
      var add  = keep.concat(missing).join(',');
      return function () { return post('/cart/update.js', { discount: add }); };
    }
    if (extra.length) {
      var rest = codes.filter(function (c) { return extra.indexOf(c.toUpperCase()) === -1; }).join(',');
      return function () { return post('/cart/update.js', { discount: rest }); };
    }
    return null;
  }

  function feeOp(cart, memberInCart) {
    var items   = cart.items || [];
    var feeLine = items.filter(function (i) { return i.handle === FEE_HANDLE; })[0];
    var payable = items.filter(function (i) { return i.handle !== FEE_HANDLE; });
    var subEx   = payable.reduce(function (s, i) { return s + i.final_line_price; }, 0);

    var shouldHave = payable.length > 0 && !isMember() && !memberInCart && subEx < FEE_THRESHOLD;
    var hasFee = !!feeLine, dupFee = !!(feeLine && feeLine.quantity > 1);

    if (shouldHave && !hasFee)  return function () { return post('/cart/add.js',    { items: [{ id: FEE_VARIANT_ID, quantity: 1 }] }); };
    if (!shouldHave && hasFee)  return function () { return post('/cart/change.js', { id: feeLine.key, quantity: 0 }); };
    if (shouldHave && dupFee)   return function () { return post('/cart/change.js', { id: feeLine.key, quantity: 1 }); };
    return null;
  }

  function reconcile(isRecheck) {
    if (running) return running;      // join the check already in progress
    busy = true;
    dirty = false;                    // snapshot point; any foreign change after this sets it back
    var recheck = false, reloadCart = false, failed = false;

    running = getCart().then(function (cart) {
      var memberInCart = (cart.items || []).some(function (i) { return i.handle === MEMBERSHIP_HANDLE; });

      var member = membershipOp(cart);
      var fee    = feeOp(cart, memberInCart);
      var promo  = promoOp(cart, memberInCart);
      if (!member && !fee && !promo) return;

      return Promise.resolve()
        .then(function () { return member ? member() : null; })
        .then(function () { return fee ? fee() : null; })
        .then(function () { return promo ? promo() : null; })
        .then(function () {
          // membership/codes changed → prices changed → re-check fees once
          recheck = !!(promo || member) && !isRecheck;
          reloadCart = !!member && isCartPage();
          suppressUntil = Date.now() + SUPPRESS_MS;
          refreshCartUI();
        });
    }).catch(function (e) {
      failed = true;
      console.error('[gsu-order-fees]', e);
    }).then(function () {
      busy = false;
      running = null;
      if (reloadCart) { window.location.reload(); return; }
      if (recheck) return reconcile(true);
      if (dirty && !failed) schedule(); // something changed the cart while we were checking
    });

    return running;
  }

  function onChange() { if (busy) return; if (Date.now() < suppressUntil) return; reconcile(false); }

  function start() { return reconcile(false); }
  if (document.readyState !== 'loading') start();
  else document.addEventListener('DOMContentLoaded', start);
  if (typeof subscribe !== 'undefined' && typeof PUB_SUB_EVENTS !== 'undefined') subscribe(PUB_SUB_EVENTS.cartUpdate, onChange);

  // Back from checkout (bfcache restore): the cart may have changed in another tab → re-check.
  window.addEventListener('pageshow', function (ev) { if (ev.persisted) { dirty = true; schedule(); } });

  window.gsuReconcileFees = start;
})();
